// API base URL normalization.
// Handles common production misconfigurations (e.g. http URL on https page).
const RAW_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

function isLoopbackHost(hostname: string): boolean {
    const value = String(hostname || '').trim().toLowerCase();
    return value === 'localhost' || value === '127.0.0.1' || value === '::1';
}

function inferWorkspaceProxyApiBase(): string | null {
    if (typeof window === 'undefined') return null;
    const path = window.location.pathname || '/';
    if (path === '/proxy/3000' || path.startsWith('/proxy/3000/')) {
        return '/proxy/8001';
    }
    return null;
}

function normalizeApiBaseUrl(raw: string): string {
    const value = String(raw || '').trim();
    const workspaceProxyApiBase = inferWorkspaceProxyApiBase();
    if (!value) return workspaceProxyApiBase || '/api';

    if (typeof window === 'undefined') {
        return value;
    }

    try {
        if (value.startsWith('/')) {
            if ((value === '/api' || value.startsWith('/api/')) && workspaceProxyApiBase) {
                return workspaceProxyApiBase;
            }
            return value.replace(/\/+$/, '') || '/api';
        }

        const parsed = new URL(value, window.location.origin);
        const currentHost = window.location.hostname;
        const sameHost = parsed.hostname === currentHost
            || (isLoopbackHost(parsed.hostname) && isLoopbackHost(currentHost));
        const normalizedPath = parsed.pathname.replace(/\/+$/, '') || '/';
        const noPath = normalizedPath === '/';
        const sameOriginPath = `${normalizedPath}${parsed.search || ''}`;

        // If app is HTTPS and API env is same-host HTTP, force same-origin /api.
        if (window.location.protocol === 'https:' && parsed.protocol === 'http:' && sameHost) {
            return workspaceProxyApiBase || '/api';
        }

        // For same-host deployments, always use reverse-proxied /api path.
        // This avoids accidental /settings (or other root paths) from env misconfiguration.
        if (sameHost) {
            if (noPath) return workspaceProxyApiBase || '/api';
            if (normalizedPath.startsWith('/proxy/')) {
                return sameOriginPath;
            }
            if (normalizedPath === '/api' || normalizedPath.startsWith('/api/')) {
                return workspaceProxyApiBase || sameOriginPath;
            }
            return workspaceProxyApiBase || '/api';
        }

        return `${parsed.origin}${normalizedPath}${parsed.search || ''}`;
    } catch {
        return workspaceProxyApiBase || '/api';
    }
}

export const API_BASE_URL = normalizeApiBaseUrl(RAW_API_BASE_URL);
